import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGripLines, faPlus, faTimes, faTrashAlt } from '@fortawesome/pro-duotone-svg-icons'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { arrayMove } from '../../../../components/component-items/sortable'
import GlobalStyles from '../../../../components/component-items/styles'
import {
  ScheduleRecurrenceEditor,
  ScheduleRecurrenceEditorValue,
} from '../../../../components/component-items/schedule-recurrence-editor'
import {
  WaveSchedule,
  WaveScheduleDraft,
  WaveScheduleTarget,
  WaveScheduleTargetOption,
  WaveScheduleTargetType,
} from './wave-schedule-types'
import {
  buildNextRuns,
  createDraftFromSchedule,
  createScheduleFromDraft,
  formatRecurrence,
  getTodayDateInTimezone,
  summarizeTargets,
  targetDisabled,
  weekdayOptions,
} from './wave-schedule-utils'

const DrawerOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1070;
  background: rgba(24, 32, 46, 0.42);
  opacity: ${({ open }) => (open ? 1 : 0)};
  visibility: ${({ open }) => (open ? 'visible' : 'hidden')};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
`

const DrawerPanel = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1080;
  width: min(660px, 100vw);
  background: #ffffff;
  box-shadow: -18px 0 44px rgba(24, 32, 46, 0.2);
  transform: translateX(${({ open }) => (open ? '0' : '100%')});
  transition: transform 0.24s ease;
  display: flex;
  flex-direction: column;

  .drawer-header,
  .drawer-footer {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #dfe7f3;
  }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    background: linear-gradient(180deg, #fbfcff 0%, #f6f8fc 100%);

    h3 {
      margin: 0;
      color: #293241;
      font-size: 1.1rem;
      font-weight: 800;
    }

    p {
      margin: 0.3rem 0 0;
      color: #687386;
      font-size: 0.85rem;
    }
  }

  .close-button {
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #687386;
    width: 2rem;
    height: 2rem;
  }

  .close-button:hover {
    background: #eef3fa;
    color: #293241;
  }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.1rem 1.25rem;
    background: #ffffff;
  }

  .drawer-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    border-top: 1px solid #dfe7f3;
    border-bottom: none;
    background: #fbfcff;
  }

  .section {
    margin-bottom: 1rem;
    padding: 1rem;
    border: 1px solid #dfe7f3;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 4px 14px rgba(34, 46, 84, 0.04);
  }

  .section-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
    color: #293241;
    font-size: 0.95rem;
    font-weight: 800;
  }

  .section-copy {
    margin-bottom: 0.8rem;
    color: #687386;
    font-size: 0.86rem;
    line-height: 1.45;
  }

  .target-toggle {
    display: inline-flex;
    gap: 0.35rem;
    margin: 1rem 0 0.85rem;
    padding: 0.25rem;
    border: 1px solid #d8e0ed;
    border-radius: 7px;
    background: #eef3fa;
  }

  .target-toggle button {
    border: 0;
    border-radius: 5px;
    padding: 0.45rem 0.8rem;
    background: transparent;
    color: #687386;
    font-weight: 800;
  }

  .target-toggle button.active {
    background: #ffffff;
    color: #2457b8;
    box-shadow: 0 1px 3px rgba(34, 46, 84, 0.12);
  }

  .target-grid {
    display: grid;
    gap: 0.55rem;
    max-height: 220px;
    overflow-y: auto;
  }

  .target-search {
    margin-bottom: 0.65rem;
  }

  .target-option,
  .selected-target,
  .blackout-row {
    border: 1px solid #e2e8f0;
    border-radius: 7px;
    background: #ffffff;
  }

  .target-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 0.9rem;
  }

  .target-option.disabled {
    opacity: 0.5;
  }

  .target-meta {
    display: flex;
    flex-direction: column;
    gap: 0.18rem;
  }

  .target-label {
    color: #293241;
    font-weight: 800;
  }

  .target-label-row {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    flex-wrap: wrap;
  }

  .target-type-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.12rem 0.46rem;
    border: 1px solid transparent;
    font-size: 0.67rem;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: 0.01em;
    text-transform: uppercase;
  }

  .target-type-pill.profile {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .target-type-pill.directive {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  .target-subtitle {
    color: #687386;
    font-size: 0.82rem;
    line-height: 1.35;
  }

  .selected-targets {
    display: grid;
    gap: 0.55rem;
  }

  .selected-target {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: 0.65rem;
    align-items: center;
    padding: 0.75rem 0.9rem;
    background: #fbfcff;
  }

  .profile-directive-toggle {
    border: 0;
    background: transparent;
    color: #2457b8;
    padding: 0;
    font-size: 0.79rem;
    font-weight: 700;
  }

  .profile-directive-toggle:hover {
    color: #1e429f;
    text-decoration: underline;
  }

  .profile-directive-preview {
    grid-column: 1 / -1;
    margin-top: 0.2rem;
    padding: 0.6rem 0.75rem;
    border: 1px solid #dbe7fb;
    border-radius: 7px;
    background: #f7faff;
  }

  .profile-directive-list {
    margin: 0;
    padding-left: 1rem;
    color: #334155;
    font-size: 0.8rem;
    line-height: 1.35;
  }

  .drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid #d8e0ed;
    border-radius: 6px;
    background: #ffffff;
    color: #687386;
    cursor: grab;
  }

  .drag-handle:active {
    cursor: grabbing;
  }

  .drag-handle:hover {
    border-color: #7aa7f8;
    background: #f3f7ff;
    color: #2457b8;
  }

  .target-order {
    width: 2rem;
    height: 2rem;
    border-radius: 7px;
    background: #eef5ff;
    color: #2457b8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
  }

  .target-actions {
    display: flex;
    gap: 0.35rem;
  }

  .icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid #d8e0ed;
    border-radius: 6px;
    background: #ffffff;
    color: #3f4b5f;
  }

  .icon-button:hover {
    border-color: #7aa7f8;
    background: #f3f7ff;
    color: #2457b8;
  }

  .preview-card {
    padding: 0.95rem 1rem;
    border-radius: 8px;
  }

  .preview-card {
    border: 1px solid #cfe0ff;
    background: linear-gradient(180deg, #f6faff 0%, #eef5ff 100%);
    color: #293241;
  }

  .company-summary {
    margin-bottom: 0.85rem;
    padding: 0.9rem 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fbfcff;
  }

  .company-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.55rem;
  }

  .company-pill {
    display: inline-flex;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .preview-list,
  .blackout-list {
    display: grid;
    gap: 0.45rem;
    margin-top: 0.7rem;
  }

  .validation-error {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    color: #dc3545;
    font-size: 0.875rem;
  }
`

type WaveScheduleDrawerProps = {
  open: boolean
  schedule: WaveSchedule | null
  initialDraft?: Partial<WaveScheduleDraft> | null
  targetOptions: WaveScheduleTargetOption[]
  targetsLoading?: boolean
  targetsError?: boolean
  warehouseTimezone: string
  onClose: () => void
  onSave: (schedule: WaveSchedule) => void
}

type SortableScheduleTargetProps = {
  index: number
  target: WaveScheduleTarget
  onRemove: (targetId: string) => void
  profileDirectiveNamesByProfileId: Map<number, string[]>
  directivesLoading: boolean
  isProfileExpanded: boolean
  onToggleProfileDirectives: (targetId: string) => void
}

const SortableScheduleTarget = ({
  index,
  target,
  onRemove,
  profileDirectiveNamesByProfileId,
  directivesLoading,
  isProfileExpanded,
  onToggleProfileDirectives,
}: SortableScheduleTargetProps) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: target.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.62 : 1,
  }

  const subtitle = target.target_type === 'wave_profile' ? '' : target.secondary_label || summarizeTargets([target])
  const profileId = target.wave_profile_id || target.parent_profile_id || null
  const profileDirectiveNames = profileId ? profileDirectiveNamesByProfileId.get(profileId) || [] : []
  const canExpandProfileDirectives = target.target_type === 'wave_profile'

  return (
    <div className="selected-target" ref={setNodeRef} style={style}>
      <button
        className="drag-handle"
        type="button"
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        aria-label={`Move ${target.label}`}
      >
        <FontAwesomeIcon icon={faGripLines} />
      </button>
      <span className="target-order">{index + 1}</span>
      <div className="target-meta">
        <span className="target-label-row">
          <span className="target-label">{target.label}</span>
          <span className={`target-type-pill ${target.target_type === 'wave_profile' ? 'profile' : 'directive'}`}>
            {target.target_type === 'wave_profile' ? 'Profile' : 'Directive'}
          </span>
        </span>
        {canExpandProfileDirectives ? (
          <div className="target-subtitle">
            <button
              className="profile-directive-toggle"
              type="button"
              onClick={() => onToggleProfileDirectives(target.id)}
            >
              {isProfileExpanded ? 'Hide' : 'View'} {profileDirectiveNames.length} directive
              {profileDirectiveNames.length === 1 ? '' : 's'}
            </button>
          </div>
        ) : null}
        {subtitle ? <span className="target-subtitle">{subtitle}</span> : null}
      </div>
      <div className="target-actions" data-no-dnd="true">
        <button
          className="icon-button"
          type="button"
          onClick={() => onRemove(target.id)}
          aria-label={`Remove ${target.label}`}
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </button>
      </div>
      {canExpandProfileDirectives && isProfileExpanded ? (
        <div className="profile-directive-preview">
          {directivesLoading ? (
            <div className="target-subtitle">Loading directives...</div>
          ) : profileDirectiveNames.length ? (
            <ul className="profile-directive-list">
              {profileDirectiveNames.map((directiveName) => (
                <li key={directiveName}>{directiveName}</li>
              ))}
            </ul>
          ) : (
            <div className="target-subtitle">No directives configured.</div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export const WaveScheduleDrawer = ({
  open,
  schedule,
  initialDraft,
  targetOptions,
  targetsLoading = false,
  targetsError = false,
  warehouseTimezone,
  onClose,
  onSave,
}: WaveScheduleDrawerProps) => {
  type DrawerValidationErrors = {
    schedule_name?: string
    recurrence?: string
    start_date?: string
    end_date?: string
    targets?: string
    blackout_dates?: string
  }

  const [draft, setDraft] = useState<WaveScheduleDraft>({
    ...createDraftFromSchedule(schedule, warehouseTimezone),
    ...(!schedule && initialDraft ? initialDraft : {}),
  })
  const [targetView, setTargetView] = useState<WaveScheduleTargetType>('wave_profile')
  const [targetSearch, setTargetSearch] = useState('')
  const [expandedProfileTargetIds, setExpandedProfileTargetIds] = useState<Record<string, boolean>>({})
  const [validationErrors, setValidationErrors] = useState<DrawerValidationErrors>({})
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const recurrenceSectionRef = useRef<HTMLDivElement | null>(null)
  const targetsSectionRef = useRef<HTMLDivElement | null>(null)
  const todayDateValue = useMemo(() => getTodayDateInTimezone(warehouseTimezone), [warehouseTimezone])
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  useEffect(() => {
    if (!open) return
    setDraft({
      ...createDraftFromSchedule(schedule, warehouseTimezone),
      ...(!schedule && initialDraft ? initialDraft : {}),
    })
    setTargetView('wave_profile')
    setTargetSearch('')
    setExpandedProfileTargetIds({})
    setValidationErrors({})
  }, [initialDraft, open, schedule, warehouseTimezone])

  const eligibleTargetOptions = useMemo(
    () => targetOptions.filter((option) => option.target_type === targetView && !targetDisabled(option, draft.targets)),
    [draft.targets, targetOptions, targetView]
  )
  const visibleTargetOptions = useMemo(() => {
    const searchValue = targetSearch.trim().toLowerCase()
    if (!searchValue) return eligibleTargetOptions

    return eligibleTargetOptions.filter((option) => {
      const searchable = [option.label, option.secondary_label].filter(Boolean).join(' ').toLowerCase()
      return searchable.includes(searchValue)
    })
  }, [eligibleTargetOptions, targetSearch])
  const companyNames = useMemo(() => {
    const names = new Set<string>()

    draft.targets.forEach((target) => {
      if (target.company_labels?.length) {
        target.company_labels.forEach((label) => names.add(label))
        return
      }

      const fallback = target.target_type === 'wave_profile' ? target.label : target.secondary_label
      if (!fallback) return
      fallback
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((name) => names.add(name))
    })

    return Array.from(names)
  }, [draft.targets])

  const profileDirectiveNamesByProfileId = useMemo(() => {
    const directivesByProfile = new Map<number, string[]>()

    targetOptions.forEach((option) => {
      if (option.target_type !== 'wave_directive' || !option.parent_profile_id) return
      const directiveNames = directivesByProfile.get(option.parent_profile_id) || []
      directiveNames.push(option.label)
      directivesByProfile.set(option.parent_profile_id, directiveNames)
    })

    return directivesByProfile
  }, [targetOptions])

  const profileDirectiveSummaries = useMemo(() => {
    const summaries = new Map<number, string>()
    profileDirectiveNamesByProfileId.forEach((directiveNames, profileId) => {
      const count = directiveNames.length
      const preview = directiveNames.slice(0, 2).join(', ')
      const remainder = count - 2
      const previewSuffix = remainder > 0 ? ` +${remainder}` : ''
      const detail = preview ? `${preview}${previewSuffix}` : 'No directives configured'
      summaries.set(profileId, `${count} directive${count === 1 ? '' : 's'}: ${detail}`)
    })

    return summaries
  }, [profileDirectiveNamesByProfileId])

  const formatTargetSubtitle = (target: WaveScheduleTargetOption) => {
    if (target.target_type === 'wave_profile') {
      const profileId = target.wave_profile_id || target.parent_profile_id
      if (targetsLoading) return 'Loading directives...'
      return profileId
        ? profileDirectiveSummaries.get(profileId) || 'No directives configured'
        : 'No directives configured'
    }

    return target.secondary_label ? `Profile: ${target.secondary_label}` : 'Profile assignment unavailable'
  }

  const nextRuns = useMemo(() => buildNextRuns(draft, 3), [draft])

  const updateTargets = (targets: WaveScheduleTarget[]) =>
    setDraft((currentDraft) => ({
      ...currentDraft,
      targets: targets.map((target, index) => ({ ...target, execution_order: index + 1 })),
    }))

  const reorderTargets = ({ active, over }: any) => {
    if (!over || active.id === over.id) return
    const activeIndex = draft.targets.findIndex((target) => target.id === active.id)
    const overIndex = draft.targets.findIndex((target) => target.id === over.id)
    if (activeIndex === -1 || overIndex === -1) return
    updateTargets(arrayMove(draft.targets, activeIndex, overIndex))
  }

  const addTarget = (target: WaveScheduleTargetOption) => {
    updateTargets(draft.targets.concat({ ...target, execution_order: draft.targets.length + 1 }))
  }

  const removeTarget = (targetId: string) => {
    updateTargets(draft.targets.filter((target) => target.id !== targetId))
    setExpandedProfileTargetIds((current) => {
      if (!current[targetId]) return current
      const { [targetId]: _removed, ...rest } = current
      return rest
    })
  }

  const toggleProfileDirectives = (targetId: string) => {
    setExpandedProfileTargetIds((current) => (current[targetId] ? {} : { [targetId]: true }))
  }

  const recurrenceValue: ScheduleRecurrenceEditorValue = {
    schedule_type: draft.schedule_type,
    recurrence_interval: draft.recurrence_interval,
    execution_times: draft.execution_times,
    days_of_week: draft.days_of_week,
    date_rule: draft.date_rule,
    day_of_month: draft.day_of_month,
    week_segment: draft.week_segment,
    day_of_week: draft.day_of_week,
    start_date: draft.start_date,
    end_date: draft.end_date,
    blackout_dates: draft.blackout_dates,
  }

  const setRecurrenceValue = (nextValue: ScheduleRecurrenceEditorValue) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      schedule_type: nextValue.schedule_type,
      recurrence_interval: nextValue.recurrence_interval,
      execution_times: nextValue.execution_times,
      days_of_week: nextValue.days_of_week,
      date_rule: nextValue.date_rule,
      day_of_month: nextValue.day_of_month,
      week_segment: nextValue.week_segment,
      day_of_week: nextValue.day_of_week,
      start_date: nextValue.start_date,
      end_date: nextValue.end_date,
      blackout_dates: nextValue.blackout_dates,
    }))
    if (
      validationErrors.start_date ||
      validationErrors.end_date ||
      validationErrors.recurrence ||
      validationErrors.blackout_dates
    ) {
      setValidationErrors((current) => ({
        ...current,
        start_date: undefined,
        end_date: undefined,
        recurrence: undefined,
        blackout_dates: undefined,
      }))
    }
  }

  const save = () => {
    const nextErrors: DrawerValidationErrors = {}

    if (!draft.name.trim()) {
      nextErrors.schedule_name = 'Schedule name is required.'
    }
    if (!draft.targets.length) {
      nextErrors.targets = 'Add at least one profile or directive to the schedule.'
    }
    if (!draft.execution_times.length) {
      nextErrors.recurrence = 'Add at least one execution time.'
    }
    if (!draft.start_date) {
      nextErrors.start_date = 'Start date is required.'
    }
    const uniqueTimes = new Set(draft.execution_times.map((t) => t.slice(0, 5)))
    if (uniqueTimes.size !== draft.execution_times.length) {
      nextErrors.recurrence = nextErrors.recurrence || 'Execution times must be unique.'
    }
    if (draft.schedule_type === 'weekly' && !draft.days_of_week.length) {
      nextErrors.recurrence = nextErrors.recurrence || 'Select at least one weekday for weekly recurrence.'
    }
    if (
      draft.schedule_type === 'monthly' &&
      draft.date_rule === 'day_of_month' &&
      (!draft.day_of_month || draft.day_of_month < 1 || draft.day_of_month > 31)
    ) {
      nextErrors.recurrence = nextErrors.recurrence || 'Monthly schedules must use a day between 1 and 31.'
    }
    if (
      draft.schedule_type === 'monthly' &&
      draft.date_rule === 'month_segment' &&
      (!draft.week_segment || !draft.day_of_week)
    ) {
      nextErrors.recurrence = nextErrors.recurrence || 'Monthly week rules require both a week and weekday.'
    }
    if (!schedule && draft.start_date && draft.start_date < todayDateValue) {
      nextErrors.start_date = nextErrors.start_date || 'Start date must be today or later.'
    }
    if (draft.start_date && draft.end_date && draft.start_date > draft.end_date) {
      nextErrors.end_date = 'End date must be on or after start date.'
    }

    const blackoutDates = draft.blackout_dates.map(({ date }) => date).filter(Boolean)
    if (blackoutDates.length !== draft.blackout_dates.length) {
      nextErrors.blackout_dates = 'Choose a date or remove the empty blackout row.'
    } else if (blackoutDates.length !== new Set(blackoutDates).size) {
      nextErrors.blackout_dates = 'Blackout dates must be unique per schedule.'
    }
    if (
      blackoutDates.some(
        (date) =>
          date < todayDateValue && !schedule?.blackout_dates.some(({ date: existingDate }) => existingDate === date)
      )
    ) {
      nextErrors.blackout_dates = 'Blackout dates cannot be in the past.'
    }

    setValidationErrors(nextErrors)

    if (Object.keys(nextErrors).length) {
      if (nextErrors.schedule_name) {
        nameInputRef.current?.focus()
        nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else if (nextErrors.recurrence || nextErrors.blackout_dates) {
        recurrenceSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else if (nextErrors.targets) {
        targetsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    onSave(createScheduleFromDraft(draft, schedule?.id))
  }

  return (
    <>
      <DrawerOverlay open={open} onClick={onClose} />
      <DrawerPanel open={open} data-pw="schedule-editor">
        <div className="drawer-header">
          <div>
            <h3>{schedule ? 'Edit Schedule' : 'Create Schedule'}</h3>
          </div>
          <button className="close-button" type="button" onClick={onClose} aria-label="Close schedule editor">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="drawer-body">
          <div className="section">
            <div className="section-heading">
              <FontAwesomeIcon icon={faGripLines} />
              Schedule Identity
            </div>
            <div className="section-copy">
              Name the schedule clearly so operators can recognize it in the calendar and management table.
            </div>
            <Form.Group>
              <Form.Label>Schedule Name</Form.Label>
              <Form.Control
                ref={nameInputRef}
                value={draft.name}
                isInvalid={Boolean(validationErrors.schedule_name)}
                onChange={(event) => {
                  setDraft({ ...draft, name: event.target.value })
                  if (validationErrors.schedule_name) {
                    setValidationErrors((current) => ({ ...current, schedule_name: undefined }))
                  }
                }}
                placeholder="Example: Morning outbound core"
              />
              <Form.Control.Feedback type="invalid">{validationErrors.schedule_name}</Form.Control.Feedback>
            </Form.Group>
          </div>

          <div ref={recurrenceSectionRef}>
            <ScheduleRecurrenceEditor
              value={recurrenceValue}
              onChange={setRecurrenceValue}
              weekdayOptions={weekdayOptions}
              timezone={draft.warehouse_timezone}
              todayDateValue={todayDateValue}
              enabledFrequencies={['daily', 'weekly', 'monthly']}
              allowMultipleTimes
              showBlackoutDates
              fieldErrors={{
                start_date: validationErrors.start_date,
                end_date: validationErrors.end_date,
                blackout_dates: validationErrors.blackout_dates,
              }}
            />
            {validationErrors.recurrence ? <div className="validation-error">{validationErrors.recurrence}</div> : null}
          </div>

          <div className="section" ref={targetsSectionRef}>
            <div className="section-heading">Scheduled Items</div>
            <div className="section-copy">
              A schedule can include multiple profiles and directives. They will launch in the configured order below.
            </div>
            <DndContext sensors={sensors} onDragEnd={reorderTargets}>
              <SortableContext items={draft.targets.map((target) => target.id)} strategy={verticalListSortingStrategy}>
                <div className="selected-targets">
                  {draft.targets.map((target, index) => (
                    <SortableScheduleTarget
                      index={index}
                      key={target.id}
                      target={target}
                      onRemove={removeTarget}
                      profileDirectiveNamesByProfileId={profileDirectiveNamesByProfileId}
                      directivesLoading={targetsLoading}
                      isProfileExpanded={Boolean(expandedProfileTargetIds[target.id])}
                      onToggleProfileDirectives={toggleProfileDirectives}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="target-toggle">
              {(['wave_profile', 'wave_directive'] as WaveScheduleTargetType[]).map((value) => (
                <button
                  className={targetView === value ? 'active' : ''}
                  key={value}
                  type="button"
                  onClick={() => {
                    setTargetView(value)
                    setTargetSearch('')
                  }}
                >
                  {value === 'wave_profile' ? 'Profiles' : 'Directives'}
                </button>
              ))}
            </div>
            <Form.Group className="target-search">
              <Form.Control
                value={targetSearch}
                onChange={(event) => setTargetSearch(event.target.value)}
                placeholder="Search by company name"
              />
            </Form.Group>
            <div className="target-grid">
              {targetsError ? (
                <div className="target-option disabled" role="alert">
                  <div className="target-meta">
                    <span className="target-label">Profiles and directives could not be loaded</span>
                    <span className="target-subtitle">
                      Refresh the page or try again before adding scheduled items.
                    </span>
                  </div>
                </div>
              ) : null}
              {targetsLoading ? (
                <div className="target-option disabled">
                  <div className="target-meta">
                    <span className="target-label">Loading profiles and directives...</span>
                    <span className="target-subtitle">Target options will appear once profile data is ready.</span>
                  </div>
                </div>
              ) : null}
              {visibleTargetOptions.map((target) => (
                <div className="target-option" key={target.id}>
                  <div className="target-meta">
                    <span className="target-label-row">
                      <span className="target-label">{target.label}</span>
                      <span
                        className={`target-type-pill ${target.target_type === 'wave_profile' ? 'profile' : 'directive'}`}
                      >
                        {target.target_type === 'wave_profile' ? 'Profile' : 'Directive'}
                      </span>
                    </span>
                    <span className="target-subtitle">{formatTargetSubtitle(target)}</span>
                  </div>
                  <GlobalStyles.Button type="button" onClick={() => addTarget(target)}>
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </GlobalStyles.Button>
                </div>
              ))}
              {!targetsLoading && !targetsError && !visibleTargetOptions.length ? (
                <div className="target-option disabled">
                  <div className="target-meta">
                    <span className="target-label">
                      {targetSearch.trim() ? 'No results for this company search' : 'No more eligible targets'}
                    </span>
                    <span className="target-subtitle">
                      {targetSearch.trim()
                        ? 'Try a different company name or clear the search.'
                        : 'Existing selections already cover or conflict with the remaining items.'}
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
            {validationErrors.targets ? <div className="validation-error">{validationErrors.targets}</div> : null}
          </div>

          <div className="section">
            <div className="company-summary">
              <div className="section-heading">Companies Included</div>
              <div className="company-list">
                {companyNames.length ? (
                  companyNames.map((companyName) => (
                    <span className="company-pill" key={companyName}>
                      {companyName}
                    </span>
                  ))
                ) : (
                  <span className="target-subtitle">No companies selected.</span>
                )}
              </div>
            </div>
            <div className="preview-card">
              <strong>Schedule Preview</strong>
              <div className="section-copy" style={{ marginBottom: 0 }}>
{formatRecurrence(draft)}
              </div>
              <div className="preview-list">
                {nextRuns.map((entry) => (
                  <div key={entry}>{entry}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="drawer-footer">
          <GlobalStyles.Button type="button" className="white" onClick={onClose}>
            Cancel
          </GlobalStyles.Button>
          <GlobalStyles.Button type="button" onClick={save}>
            Save Schedule
          </GlobalStyles.Button>
        </div>
      </DrawerPanel>
    </>
  )
}
